import type { ReactNode } from "react";
import type { RowData } from "@tanstack/react-table";
import { useDataGridContext } from "../DataGridContext";
import type { DataGridTable } from "../columns/types";
import { GATE_ROW_COUNT } from "../layout/density";
import { SkeletonRows } from "./SkeletonRows";
import styles from "./states.module.scss";

interface DataGridGateProps {
    /** The call to action over the locked rows, e.g. an upgrade button. */
    children: ReactNode;
}

interface GateOverlayProps<TData extends RowData> {
    table: DataGridTable<TData>;
    children: ReactNode;
}

const GateOverlay = <TData extends RowData>({
    table,
    children,
}: Readonly<GateOverlayProps<TData>>) => (
    <div className={styles.gate} data-testid="data-grid-gate">
        <SkeletonRows
            table={table}
            rowCount={GATE_ROW_COUNT}
            className={styles.gateRows}
        />
        <div className={styles.gateOverlay}>{children}</div>
    </div>
);

/**
 * Locked rows below a gated table (`gate.visibleRowCount`): blurred
 * placeholder rows under the consumer's call to action. The hook already
 * drops the hidden rows, so sorting or search can't reveal them. Renders
 * nothing without `gate`, before the first load, or when no rows are held back.
 */
export const DataGridGate = ({ children }: Readonly<DataGridGateProps>) => {
    const { visit, status } = useDataGridContext();
    return visit((instance) =>
        instance.gate && status.state === "ready" ? (
            <GateOverlay table={instance.table}>{children}</GateOverlay>
        ) : null,
    );
};
